import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  ClinicalPageShell,
  SignalBar,
  SectionPanel,
  ClinicalDataGrid,
  StatusPill,
  tokens,
  fmtNumber,
} from "../../shared/clinical-design";
import HealthStatusChart from "../components/HealthStatusChart";
import { getClinicalOperations } from "../services/clinicalOpsService";

/** Celda numérica con tono según umbral (más alto = peor). */
const TonedNumber = ({ value, warnAt, critAt, suffix = "" }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: tokens.fontMono,
      fontVariantNumeric: "tabular-nums",
      fontSize: 13,
      color: value >= critAt ? tokens.crit : value >= warnAt ? tokens.warn : tokens.ink,
    }}
  >
    {typeof value === "number" && !Number.isInteger(value) ? value.toFixed(1) : fmtNumber(value)}
    {suffix}
  </Typography>
);

const centerColumns = [
  { key: "label", header: "Center" },
  {
    key: "totalPatients",
    header: "Patients",
    align: "right",
    mono: true,
    render: (r) => fmtNumber(r.totalPatients),
  },
  {
    key: "currentInpatient",
    header: "Inpatient now",
    align: "right",
    render: (r) => <TonedNumber value={r.currentInpatient} warnAt={5} critAt={10} />,
  },
  {
    key: "highRiskCensus",
    header: "High-risk census",
    align: "right",
    mono: true,
    render: (r) => fmtNumber(r.highRiskCensus),
  },
  {
    key: "unmanagedHighRisk",
    header: "Unmanaged HR",
    align: "right",
    render: (r) => <TonedNumber value={r.unmanagedHighRisk} warnAt={100} critAt={200} />,
  },
  {
    key: "postDischargePending",
    header: "Post-DC pending",
    align: "right",
    render: (r) => <TonedNumber value={r.postDischargePending} warnAt={10} critAt={25} />,
  },
  {
    key: "erFrequentFlyers",
    header: "ER freq. flyers",
    align: "right",
    mono: true,
    render: (r) => fmtNumber(r.erFrequentFlyers),
  },
  {
    key: "openCareGaps",
    header: "Open care gaps",
    align: "right",
    render: (r) => <TonedNumber value={r.openCareGaps} warnAt={1200} critAt={1600} />,
  },
  {
    key: "visitsToday",
    header: "Visits today",
    align: "right",
    mono: true,
    render: (r) => fmtNumber(r.visitsToday),
  },
  {
    key: "noShowRate",
    header: "No-show",
    align: "right",
    render: (r) => <TonedNumber value={r.noShowRate} warnAt={12} critAt={15} suffix="%" />,
  },
  {
    key: "cmStatus",
    header: "CM engagement",
    render: (r) =>
      r.inActiveCM > 0 ? (
        <StatusPill tone="ok" label={`${r.inActiveCM} in CM`} dense />
      ) : (
        <StatusPill tone="crit" label="No active CM" dense />
      ),
  },
];

/**
 * Module L — Clinical Operations Dashboard.
 * Vista de control para coordinadores/managers: carga operativa por centro,
 * backlog clínico y distribución de health status.
 */
export default function ClinicalOperationsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await getClinicalOperations();
        if (active) setData(result);
      } catch (e) {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const s = data?.signals;
  const signals = s
    ? [
        { label: "Visits today", value: fmtNumber(s.visitsToday), tone: "info" },
        {
          label: "No-show rate",
          value: `${s.noShowRate.toFixed(1)}%`,
          tone: s.noShowRate >= 12 ? "warn" : "ok",
          hint: "Network weighted average, current month",
        },
        {
          label: "Post-discharge pending",
          value: fmtNumber(s.postDischargePending),
          tone: s.postDischargePending > 50 ? "warn" : "info",
          hint: "Discharges awaiting follow-up contact",
        },
        {
          label: "Unmanaged high risk",
          value: fmtNumber(s.unmanagedHighRisk),
          tone: "crit",
          hint: "High-risk patients without active care management",
        },
        {
          label: "Open care gaps",
          value: fmtNumber(s.openCareGaps),
          tone: "warn",
        },
      ]
    : [];

  return (
    <ClinicalPageShell
      title="Clinical Operations"
      subtitle="Network-wide operational control: workloads, clinical backlog and risk distribution across centers."
      moduleTag="Module L"
      devPreview
    >
      <SignalBar signals={signals} sx={{ mb: 2 }} />

      <SectionPanel title="Center operations" contentSx={{ p: 0 }} sx={{ mb: 2 }}>
        <ClinicalDataGrid
          columns={centerColumns}
          rows={data?.centers || []}
          rowKey={(r) => r.key}
          loading={loading}
          emptyMessage="No operational data available for any center."
          maxHeight={460}
          sx={{ border: 0 }}
        />
      </SectionPanel>

      <SectionPanel title="Health status distribution by center">
        <HealthStatusChart centers={data?.centers || []} loading={loading} />
      </SectionPanel>
      <Box sx={{ height: 8 }} />
    </ClinicalPageShell>
  );
}
